import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { UseMutateAsyncFunction } from 'react-query';
import {
  FORM_ID,
  FormValues,
  AllocationStrategyType,
  createValidation,
  ManageExcessCashDTO,
  hasDebtManagementGoalOption,
  includesDebtManagementEarmark,
  isDebtManagementOnlyEarmark,
} from '../ManageExcessCashModal.meta';
import { GoalEarmark } from './GoalEarmark';
import { LoanPayoffStrategy } from './LoanPayoffStrategy';
import { AllocateExcessCashMethod } from './AllocateExcessCashMethod';
import { MarketAssumptions } from './MarketAssumptions/MarketAssumptions';
import { HypotheticalReturn } from './HypotheticalReturn/HypotheticalReturn';
import { PortfolioGroup } from './PortfolioGroup/PortfolioGroup';
import { PortfolioGroupChart } from './PortfolioGroupChart/PortfolioGroupChart';
import { SaveExcessCashParams } from '../api/useManageExcessCashMutation';
import { toSaveExcessCashParams } from '../toSaveExcessCashParams';
import { useExcessCashModalForm } from './useExcessCashModalForm';
import { ExistingAccount } from './ExistingAccount/ExistingAccount';

interface Props {
  saveExcessCash: UseMutateAsyncFunction<any, Error, SaveExcessCashParams, unknown>;
  showGoalEarmark?: boolean;
  data: ManageExcessCashDTO;
  onClose(): void;
}

export const ExcessCashModalForm: React.FC<Props> = ({
  showGoalEarmark = false,
  data,
  onClose,
  saveExcessCash,
}) => {
  const {
    modelPortfoliosGroups,
    options: {
      excessCashAllocationTypes,
      excessCashAllocation,
      defaultAssetAllocation,
      accountList,
      earmarkOptions,
      loanPayoffStrategyOptions,
      loanPayoffStrategy: hydratedLoanPayoffStrategy,
    },
  } = data;

  const { marketAssumptions } = useExcessCashModalForm(defaultAssetAllocation);
  const hasDebtManagementGoal = hasDebtManagementGoalOption(earmarkOptions);

  const formBag = useForm<FormValues>({
    resolver: yupResolver(createValidation(hasDebtManagementGoal)),
    defaultValues: {
      ExcessCashGoalEarmark: excessCashAllocation?.excessCashGoalEarmark,
      LoanPayoffStrategy:
        excessCashAllocation?.loanPayoffStrategy ?? hydratedLoanPayoffStrategy ?? null,
      ExcessCashFlowStrategy: {
        type: excessCashAllocation?.excessCashAllocationType,
        data: {
          hypotheticalReturnPercent:
            excessCashAllocation?.excessCashAllocationDetails?.hypotheticalReturnPercent,
          marketAssumptions: excessCashAllocation?.excessCashAllocationDetails?.marketAssumptions?.map(
            item => ({
              ...item,
              y: item.y * 100,
            }),
          ),
          modelPortfolios: {
            ...excessCashAllocation?.excessCashAllocationDetails?.modelPortfolios,
          },
          existingAccountId: excessCashAllocation?.excessCashAllocationDetails?.existingAccountId,
        },
      },
    },
  });

  const [
    ExcessCashFlowStrategyType,
    ExcessCashFlowStrategyData,
    selectedGoalEarmarks,
  ] = formBag.watch([
    'ExcessCashFlowStrategy.type',
    'ExcessCashFlowStrategy.data',
    'ExcessCashGoalEarmark',
  ]);

  const hasDebtManagementEarmark = selectedGoalEarmarks?.some(earmark =>
    includesDebtManagementEarmark(earmark, hasDebtManagementGoal),
  );
  const shouldSkipSavedCashSource = isDebtManagementOnlyEarmark(selectedGoalEarmarks);

  const onSubmit = async (values: FormValues) => {
    try {
      await saveExcessCash(toSaveExcessCashParams(values, hasDebtManagementGoal));
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <FormProvider {...formBag}>
      <form
        onSubmit={formBag.handleSubmit(onSubmit)}
        id={FORM_ID}
        css={`
          display: flex;
          flex-direction: column;
          gap: 16px;
        `}
      >
        {showGoalEarmark
          && (shouldSkipSavedCashSource
            || ExcessCashFlowStrategyType !== AllocationStrategyType.ExistingAccount) && (
          <>
            <GoalEarmark earmarkOptions={earmarkOptions} />
            {hasDebtManagementEarmark && (
              <LoanPayoffStrategy options={loanPayoffStrategyOptions ?? []} />
            )}
          </>
        )}
        {!shouldSkipSavedCashSource && (
          <>
            <AllocateExcessCashMethod
              excessCashAllocationOptions={excessCashAllocationTypes}
              marketAssumptions={marketAssumptions}
            />

            {ExcessCashFlowStrategyType === AllocationStrategyType.MarketAssumptions ? (
              <MarketAssumptions />
            ) : ExcessCashFlowStrategyType === AllocationStrategyType.PortfolioGroup ? (
              <>
                <PortfolioGroup options={modelPortfoliosGroups} />

                {ExcessCashFlowStrategyData?.modelPortfolios && (
                  <PortfolioGroupChart
                    data={ExcessCashFlowStrategyData.modelPortfolios.assetAllocationInputModel}
                    chartTitle={ExcessCashFlowStrategyData.modelPortfolios.portfolioName}
                    assetAllocationList={defaultAssetAllocation}
                  />
                )}
              </>
            ) : ExcessCashFlowStrategyType === AllocationStrategyType.HypotheticalReturn ? (
              <HypotheticalReturn />
            ) : (
              ExcessCashFlowStrategyType === AllocationStrategyType.ExistingAccount && (
                <ExistingAccount options={accountList} />
              )
            )}
          </>
        )}
      </form>
    </FormProvider>
  );
};
